import React from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider, Waitlist } from '@clerk/clerk-react';
import App from './App';
import './index.css';

// ─── Routing ─────────────────────────────────────────────────────────
// The app has no router, and one path is all we need: /waitlist renders
// Clerk's <Waitlist /> instead of the builder. Deciding here rather than
// inside App keeps it clear of App's hooks — an early return in there
// would sit above a long list of useState calls.
//
// Marketing lives at / and is served as a static file (see vercel.json),
// so it never reaches React at all. Everything else falls through to the
// SPA and renders the builder.
//
// The trailing-slash strip means /waitlist and /waitlist/ both match.
const PATH = window.location.pathname.replace(/\/+$/, '') || '/';
const IS_WAITLIST = PATH === '/waitlist';

// Pull publishable key from env — CRA requires the REACT_APP_ prefix
// for any var to be visible in frontend code
const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Fail loudly if missing — beats a cryptic Clerk runtime error later
if (!PUBLISHABLE_KEY) {
  throw new Error('Missing REACT_APP_CLERK_PUBLISHABLE_KEY in .env.local');
}

// Font stack used by Clerk's modals + dropdowns. Helvetica with sensible fallbacks
// so it still looks decent if Helvetica isn't installed on the user's system.
const CLERK_FONT = 'Helvetica, "Helvetica Neue", Arial, sans-serif';

// Access-request page. Clerk is in Restricted Mode, so sign-up isn't
// self-serve — <Waitlist /> collects the request and you approve it from the
// Clerk dashboard. Requires Sign-up mode = Waitlist to be set there; until it
// is, the component renders but submissions are rejected.
function WaitlistPage() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        padding: '40px 20px',
        background: '#0a1628',
        fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <h1 style={{ margin: 0, color: '#e8f4ff', fontSize: 24, fontWeight: 700 }}>
          Request access
        </h1>
        <p style={{ margin: '10px 0 0', color: '#8aa8c5', fontSize: 14, lineHeight: 1.6 }}>
          The AI tools and presentation builder are invite-only while in beta.
          Leave your email and we'll enable your account and let you know once
          it's active.
        </p>
      </div>

      <Waitlist />

      {/* Plain anchor, not a router link — the landing page is a static file
          outside the React app. */}
      <a href="/" style={{ color: '#3399ff', fontSize: 13, textDecoration: 'none' }}>
        ← Back to ShopBuilder Pro
      </a>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      appearance={{
        // Global theme variables — apply to all Clerk components
        variables: {
          colorPrimary: '#3399ff',
          colorBackground: '#0a1628',
          colorInputBackground: '#07111e',
          // Primary text — bright, high contrast
          colorText: '#e8f4ff',
          // Secondary/muted text — labels, descriptions
          colorTextSecondary: '#8aa8c5',
          colorTextOnPrimaryBackground: '#ffffff',
          colorInputText: '#e8f4ff',
          // Neutral palette — Clerk uses this for greys including dropdown item text.
          // Forcing it to light prevents the black-on-dark issue in UserButton menu.
          colorNeutral: '#e8f4ff',
          // Status colors
          colorDanger: '#ff6b6b',
          colorSuccess: '#40c060',
          colorWarning: '#ffcc00',
          // Typography
          fontFamily: CLERK_FONT,
          fontFamilyButtons: CLERK_FONT,
          fontSize: '13px',
          borderRadius: '8px',
        },
        // Element-level overrides — defense in depth for the UserButton popover
        // since theme variables don't always cascade to every element correctly.
        elements: {
          // The popover card that appears below the avatar
          userButtonPopoverCard: {
            backgroundColor: '#0a1628',
            border: '1px solid #162840',
            fontFamily: CLERK_FONT,
          },
          // Top section with user's name + email
          userButtonPopoverMainIdentifier: {
            color: '#e8f4ff',
          },
          userButtonPopoverMainIdentifierText: {
            color: '#e8f4ff',
          },
          // The "Manage account" / "Sign out" action buttons
          userButtonPopoverActionButton: {
            color: '#e8f4ff',
            fontFamily: CLERK_FONT,
          },
          userButtonPopoverActionButtonText: {
            color: '#e8f4ff',
          },
          userButtonPopoverActionButtonIcon: {
            color: '#8aa8c5',
          },
          // The footer with "Secured by Clerk"
          userButtonPopoverFooter: {
            color: '#8aa8c5',
          },
          // Catch-all: force the font on all Clerk-rendered elements
          rootBox: {
            fontFamily: CLERK_FONT,
          },
          card: {
            fontFamily: CLERK_FONT,
            boxShadow: '0 8px 40px rgba(0,0,0,.6)',
          },
        },
      }}
    >
      {IS_WAITLIST ? <WaitlistPage /> : <App />}
    </ClerkProvider>
  </React.StrictMode>
);
